import {
  TmElement,
  base,
  reset,
  body,
  spacing,
  token,
} from '@thiememeulenhoff/lit-sdk';
import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { tagName } from '../../helpers';

const tag = tagName('message');

@customElement(tag)
export class MessageComponent extends TmElement {
  @property({ type: Boolean, reflect: true })
  clickable?: boolean;

  @property({ type: Boolean, reflect: true })
  type?: boolean;

  static override styles = [
    base.focus,
    reset.button,
    css`
      :where(div),
      :where(footer) {
        display: flex;
        font-size: 1em;
        gap: ${spacing(100)};
        inline-size: 100%;
        justify-content: space-between;
      }
      :where(section),
      :where(button.section) {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        text-align: start;
      }
      :where(header) {
        font-weight: 600;

        .type {
          ${body('small', 'bold')};
        }
      }
      :where(footer) {
        color: ${token('message-footer-foreground-default')};
        margin-block-start: ${spacing(50)};

        .type {
          ${body('small')};
          font-weight: 300;
        }
      }

      :where(a-icon) {
        margin-block-start: ${spacing(50)};
      }

      .main-type {
        ${body('small')};
        font-weight: 300;
      }
    `,
  ];

  #footerTemplate() {
    return html`<footer>
      <span class="${this.type ? 'type' : ''}"
        ><slot name="footer"></slot
      ></span>
    </footer>`;
  }

  #clickableSection(
    content: unknown,
    handler: unknown,
    testId?: string,
    ariaLabel?: string,
  ) {
    if (this.clickable) {
      return html`<button
        class="section"
        @click="${handler}"
        appAnalyseClick="click-message"
        data-test="${testId ?? nothing}"
        aria-label="${ariaLabel ?? nothing}"
      >
        ${content}
      </button>`;
    }
    return html`<section>${content}</section>`;
  }

  override render() {
    const content = this.#clickableSection(
      html`
        <header>
          <slot name="title" class="${this.type ? 'type' : ''}"></slot>
        </header>
        <div class="main ${this.type ? 'main-type' : ''}"><slot></slot></div>
      `,
      this.#handleClickContent,
      'content',
    );

    return html`<div ${this.type ? 'type' : ''}>${content}</div>
      ${this.#footerTemplate()}`;
  }

  #handleClickContent = (event: MouseEvent) => {
    this.#handleClick(event, 'clicked');
  };

  #handleClick = (event: MouseEvent, eventName: string) => {
    this.dispatchLocalEvent({
      eventName,
      detail: event,
    });
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-message': MessageComponent;
  }
}
